import * as React from 'react'

import { ReadAloudButtonVariant } from './constants'
import { ReadAloudButton } from './readAloudButton'
import { useReadAloud } from './readAloudContext'
import { annotateForScreenreader, buildSection } from './readAloudKaraoke'

import { safeParseHtml } from '../../libs/safeHtml'
import { ClipSource } from '../../libs/speech/clipSource'
import { buildWordTimeline, wordIndexForCharIndex } from '../../libs/speech/speechText'
import { speechTextFromWords } from '../../libs/speech/wordCollector'

interface ReadAloudSectionSource {
  annotateBold?: boolean;
  content?: React.ReactNode;
  html?: string;
  id?: string;
  source?: ClipSource | null;
  speechText?: string;
  trailingSpeechText?: string;
}

interface ReadAloudSectionProps extends ReadAloudSectionSource {
  buttonVariant?: ReadAloudButtonVariant;
  className?: string;
  focusOnDark?: boolean;
  label?: string;
}

const useReadAloudSection = ({ annotateBold, content, html, id, source, speechText: givenSpeechText, trailingSpeechText, }: ReadAloudSectionSource) => {
  const { readAloudEnabled, } = useReadAloud()
  const generatedId = React.useId()
  const contentId = id || generatedId
  const [spokenWordIndex, setSpokenWordIndex] = React.useState<number | null>(null)

  const { speechText, timeline, wordCount, } = React.useMemo(() => {
    const { words, } = buildSection({ annotateBold, content, html, spokenWordIndex: null, })
    const text = givenSpeechText ?? speechTextFromWords(trailingSpeechText ? [...words, trailingSpeechText] : words)
    const spokenTimeline = buildWordTimeline(text)
    const highlightable = givenSpeechText === undefined || spokenTimeline.length === words.length
    return { speechText: text, timeline: spokenTimeline, wordCount: highlightable ? words.length : 0, }
  }, [annotateBold, content, givenSpeechText, html, trailingSpeechText])

  const { rendered, } = React.useMemo(() => buildSection({ annotateBold, content, html, spokenWordIndex, }), [annotateBold, content, html, spokenWordIndex])

  const handleBoundary = React.useCallback((charIndex: number) => {
    const wordIndex = wordIndexForCharIndex(timeline, charIndex)
    setSpokenWordIndex(wordIndex !== null && wordIndex < wordCount ? wordIndex : null)
  }, [timeline, wordCount])

  const handleSpeechEnd = React.useCallback(() => setSpokenWordIndex(null), [])

  const plainContent = readAloudEnabled ? null : (html != null ? safeParseHtml(annotateBold ? annotateForScreenreader(html) : html) : content)

  return {
    contentId,
    readAloudEnabled,
    readAloudButtonProps: { controlsId: contentId, onBoundary: handleBoundary, onSpeechEnd: handleSpeechEnd, source, text: speechText, },
    rendered: readAloudEnabled ? rendered : plainContent,
  }
}

const FIRST_LINE_HEIGHT_PROPERTY = '--read-aloud-first-line'
const FIRST_LINE_TOP_PROPERTY = '--read-aloud-first-line-top'

const firstSpokenTextNode = (element: HTMLElement) => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: node => (node.textContent || '').trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  })
  return walker.nextNode()
}

const firstLineBox = (element: HTMLElement) => {
  try {
    const textNode = firstSpokenTextNode(element)
    if (!textNode) { return null }

    const range = document.createRange()
    range.selectNodeContents(textNode)
    const [firstRect] = Array.from(range.getClientRects() || [])
    if (!firstRect?.height) { return null }

    return { height: Math.round(firstRect.height), top: Math.round(firstRect.top - element.getBoundingClientRect().top), }
  } catch {
    return null
  }
}

const useFirstLineStyle = (contentRef: React.RefObject<HTMLElement>, rendered: React.ReactNode) => {
  const [firstLine, setFirstLine] = React.useState<{ height: number, top: number } | null>(null)

  React.useLayoutEffect(() => {
    const element = contentRef.current
    if (!element) { return }

    const measure = () => setFirstLine((previous) => {
      const next = firstLineBox(element)
      return next && (!previous || next.height !== previous.height || next.top !== previous.top) ? next : previous
    })

    measure()

    if (typeof ResizeObserver === 'undefined') { return }

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [contentRef, rendered])

  if (!firstLine) { return undefined }

  return {
    [FIRST_LINE_HEIGHT_PROPERTY]: `${firstLine.height}px`,
    [FIRST_LINE_TOP_PROPERTY]: `${Math.max(firstLine.top, 0)}px`,
  } as React.CSSProperties
}

const ReadAloudSection = ({ annotateBold, buttonVariant, className, content, focusOnDark, html, id, label, source, trailingSpeechText, }: ReadAloudSectionProps) => {
  const { contentId, readAloudEnabled, readAloudButtonProps, rendered, } = useReadAloudSection({ annotateBold, content, html, id, source, trailingSpeechText, })
  const contentRef = React.useRef<HTMLDivElement>(null)
  const firstLineStyle = useFirstLineStyle(contentRef, rendered)

  if (!readAloudEnabled) {
    if (className) { return <div className={className}>{rendered}</div> }
    return rendered
  }

  return (
    <div className="prompt-and-read-aloud" style={firstLineStyle}>
      <div className={`read-aloud-content ${className || ''}`} id={contentId} ref={contentRef}>{rendered}</div>
      <ReadAloudButton focusOnDark={focusOnDark} label={label} variant={buttonVariant} {...readAloudButtonProps} />
    </div>
  )
}

export { ReadAloudSection, useFirstLineStyle, useReadAloudSection }
